/**
 * Phone validation utilities using libphonenumber-js.
 * Returns translation keys for errors instead of translated messages.
 */

import parsePhoneNumberLib from 'libphonenumber-js';

/**
 * Optional settings for phone validation.
 */
export interface PhoneValidationOptions {
  /** Whether the field is required (default: true). Empty string is invalid if required. */
  required?: boolean;

  /** ISO country code (e.g., "CA", "US") to use when parsing numbers without a '+' prefix. */
  defaultCountry?: string;
}

/**
 * Result of phone validation.
 */
export interface PhoneValidationResult {
  /** Whether the phone number is valid */
  isValid: boolean;

  /** Normalized phone number (always begins with '+' if not empty) */
  normalized: string;

  /** International formatted version, if valid (e.g., "+1 416 555 1234") */
  formatted?: string;

  /** ISO country code (e.g., "US", "CA"), if valid and determinable */
  countryCode?: string;

  /** i18n translation key for the error, or null if valid */
  errorKey: string | null;
}

/**
 * Validates a phone number and returns structured results with error translation keys.
 */
export const validatePhoneNumber = (
  raw: string,
  { required = true, defaultCountry }: PhoneValidationOptions = {},
): PhoneValidationResult => {
  const trimmed = raw?.trim() || '';

  if (!trimmed) {
    if (!required) {
      return {
        isValid: true,
        normalized: '',
        formatted: undefined,
        countryCode: undefined,
        errorKey: null,
      };
    }
    return {
      isValid: false,
      normalized: '',
      formatted: undefined,
      countryCode: undefined,
      errorKey: 'phone_number_required',
    };
  }

  try {
    // Use defaultCountry for numbers without '+' prefix
    const parsed = trimmed.startsWith('+')
      ? parsePhoneNumberLib(trimmed)
      : parsePhoneNumberLib(trimmed, defaultCountry?.toUpperCase() as any);

    if (!parsed?.isValid()) {
      return {
        isValid: false,
        normalized: parsed?.number || trimmed,
        formatted: undefined,
        countryCode: undefined,
        errorKey: 'phone_number_invalid',
      };
    }

    return {
      isValid: true,
      normalized: parsed.number,
      formatted: parsed.formatInternational(),
      countryCode: parsed.country || undefined,
      errorKey: null,
    };
  } catch (error) {
    return {
      isValid: false,
      normalized: trimmed,
      formatted: undefined,
      countryCode: undefined,
      errorKey: 'phone_number_invalid',
    };
  }
};
